/**
 * SupabaseFactory - Platform-agnostic Supabase client provider
 *
 * This factory allows BricksOS services to remain platform-agnostic
 * while still having access to a Supabase client.
 *
 * Usage in BricksOS services:
 * ```javascript
 * import { getSupabaseClient } from '../../shared/SupabaseFactory.js';
 *
 * class MyService {
 *   static async doSomething(options = {}) {
 *     const supabase = options.supabaseClient || getSupabaseClient();
 *     // ... use supabase
 *   }
 * }
 * ```
 *
 * Platform initialization:
 * - Web: SupabaseFactory.setClient(supabase) in main.jsx
 * - Mobile: SupabaseFactory.setClient(supabase) in App.js
 */

let supabaseClient = null;
let supabaseAdminClient = null;
let browserAdminWarningShown = false;

export const SupabaseFactory = {
  /**
   * Set the Supabase client for this platform
   * @param {Object} client - Supabase client instance
   * @param {Object} adminClient - Optional admin client with elevated permissions
   */
  setClient(client, adminClient = null) {
    if (!client) {
      throw new Error('SupabaseFactory.setClient: client is required');
    }
    supabaseClient = client;
    if (adminClient) {
      supabaseAdminClient = adminClient;
    }
  },

  /**
   * Get the current Supabase client
   * @returns {Object} Supabase client instance
   * @throws {Error} If client hasn't been initialized
   */
  getClient() {
    if (!supabaseClient) {
      throw new Error(
        'SupabaseFactory: Client not initialized. ' +
        'Call SupabaseFactory.setClient() in your platform entry point ' +
        '(main.jsx for web, App.js for mobile)'
      );
    }
    return supabaseClient;
  },

  /**
   * Get the admin Supabase client (elevated permissions)
   * @returns {Object} Supabase admin client instance
   * @throws {Error} If admin client hasn't been initialized
   */
  getAdminClient() {
    // SECURITY: Admin client not available in browser environment
    // Always use regular client with proper authentication
    if (typeof window !== 'undefined' && !browserAdminWarningShown) {
      console.warn('SupabaseFactory: Using regular client for security. Ensure proper RLS policies are in place.');
      browserAdminWarningShown = true;
    }
    return this.getClient();
  },

  /**
   * Check if client has been initialized
   * @returns {boolean}
   */
  isInitialized() {
    return supabaseClient !== null;
  },

  /**
   * Check if admin client has been initialized
   * @returns {boolean}
   */
  isAdminInitialized() {
    return supabaseAdminClient !== null;
  },

  /**
   * Reset the clients (useful for testing)
   */
  reset() {
    supabaseClient = null;
    supabaseAdminClient = null;
  }
};

/**
 * Convenience function to get the Supabase client
 * @returns {Object} Supabase client instance
 */
export const getSupabaseClient = () => SupabaseFactory.getClient();

/**
 * Convenience function to get the Supabase admin client
 * SECURITY: Returns regular client in browser for security
 * @returns {Object} Supabase client instance
 */
export const getSupabaseAdmin = () => SupabaseFactory.getAdminClient();

export default SupabaseFactory;
